import Vue from 'vue';

// Private functions
function normalizeAppointmentLanguage(language) {
  if (language == null || language === '') {
    return null;
  }

  const normalizedLanguage = language.toString().trim().replace('-', '_').toLowerCase();

  if (normalizedLanguage.indexOf('fr') === 0) {
    return 'fr_BE';
  }

  if (normalizedLanguage.indexOf('nl') === 0) {
    return 'nl_BE';
  }

  return null;
}

function getAppointmentUpdateLanguage(state) {
  const driverLanguage = state.customerDataRaw != null && state.customerDataRaw.driver != null
    ? state.customerDataRaw.driver.language
    : null;

  return normalizeAppointmentLanguage(driverLanguage)
    || normalizeAppointmentLanguage(Vue.config.language)
    || 'nl_BE';
}

function determineAppointmentType(state) {
  let appointmentType = '';
  if (state.serviceType === 'maintenance') {
    return 'aw';
  }

  switch (state.tireType) {
    case 'ms': // winter
      if (state.serviceType === 'change') {
        if ((state.bodyworkType === 'car' && state.tireStock.ms === 4) || (state.bodyworkType === 'bus' && state.tireStock.ms === 6)) {
          appointmentType = 'zw';
        } else {
          appointmentType = 'zwn';
        }
      } else {
        appointmentType = 'nw';
      }
      return appointmentType;
    case 'tz': // zomer
      if (state.serviceType === 'change') {
        if ((state.bodyworkType === 'car' && state.tireStock.tz === 4) || (state.bodyworkType === 'bus' && state.tireStock.tz === 6)) {
          appointmentType = 'wz';
        } else {
          appointmentType = 'wzn';
        }
      } else {
        appointmentType = 'nz';
      }
      return appointmentType;
    case 'ts': // season
      if (state.serviceType === 'change') {
        if ((state.bodyworkType === 'car' && state.tireStock.ts === 4) || (state.bodyworkType === 'bus' && state.tireStock.ts === 6)) {
          appointmentType = 'wz';
        } else {
          appointmentType = 'wzn';
        }
      } else {
        appointmentType = 'na';
      }
      return appointmentType;
    default:
      return appointmentType;
  }
}

const APPOINTMENT_GETTERS = {
  getLanguage: state => state.language,
  getLoadingState: state => state.status.loadingState,
  getError: state => state.status.error,
  getErrorSlot: state => state.status.errorSlot,
  getLicensePlate: state => state.licensePlate,
  getVehiculeType: state => state.vehiculeType,
  getTireLocations: state => state.tireLocations,
  getTireStock: state => state.tireStock,
  getBodyworkType: state => state.bodyworkType,
  getTireType: state => state.tireType,
  getServiceType: state => state.serviceType,
  getRunFlat: state => state.runFlat,
  getRegion: state => state.region,
  getHasAppointment: state => {
    if (state.appointment === null) {
      return false;
    }
    if (state.appointment.hasAppointment === true) {
      return true;
    }
    if (state.appointment.addressId === '') {
      return false;
    }
    if (state.appointment.depotId === 0) {
      return false;
    }
    return true;
  },
  getTimeOfArrival: state => state.timeOfArrival,
  getSearchWatch: state => state.searchWatch,
  getMomentsOfTimeOfArrival: state => state.momentsOfTimeOfArrival,
  getTireSizes: state => state.tireSizes,
  getPersonalData: state => state.personalData,
  getVehicleData: state => state.vehicleData,
  getDepotId: state => state.depotId,
  getFavouredDepotId: state => state.favouredDepotId,
  getDepotIds: state => state.depotIds,
  getMaintenanceDepotIds: state => state.maintenanceDepotIds,
  getAddresses: state => state.addresses,
  getAddressId: state => state.addressId,
  getRegionData: state => state.regionData,
  getSlotNumber: state => state.slotNumber,
  getCat: state => state.cat,
  getDate: state => state.slotDate,
  getPostalCode: state => state.postalCode,
  getRegionCode: state => state.regionCode,
  getModalTpp: state => state.tppModal,
  getHour: state => state.slotHour,
  getChassie: state => state.chassie,
  getIsValidAppointment: state => {
    if (state.slotNumber === '' || state.depotId === 0) {
      return false;
    }

    return true;
  },
  getIsValidAppointmentMobileComp: state => {
    if (state.addressId === '' || state.slotDate === '') {
      return false;
    }
    return true;
  },
  getAppointmentPostObject: state => {
    // Only send changed driver information
    const changedDriverInfo = {};
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.title !== state.personalData.title) {
      changedDriverInfo.title = state.personalData.title;
    }
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.firstName !== state.personalData.firstName) {
      changedDriverInfo.firstName = state.personalData.firstName;
    }
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.lastName !== state.personalData.lastName) {
      changedDriverInfo.surName = state.personalData.lastName;
    }
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.email !== state.personalData.email) {
      changedDriverInfo.email = state.personalData.email;
    }
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.cellphoneNumber !== state.personalData.cellphoneNumber) {
      changedDriverInfo.mobile = state.personalData.cellphoneNumber;
    }
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.zipCode !== state.personalData.zipCode) {
      changedDriverInfo.postalCode = state.personalData.zipCode;
    }
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.city !== state.personalData.city) {
      changedDriverInfo.city = state.personalData.city;
    }
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.address !== state.personalData.address) {
      changedDriverInfo.address = state.personalData.address;
    }

    const isUnknownParticulierMaintenance = state.customerDataRaw.customerType === 'unknown' && state.vehiculeType === 'part' && state.serviceType === 'maintenance';

    if (!isUnknownParticulierMaintenance && (state.customerDataRaw.driver === null || state.customerDataRaw.driver.leasingCompanyId !== state.personalData.leasingCompanyId)) {
      changedDriverInfo.leaseId = state.personalData.leasingCompanyId;
    }
    if (!isUnknownParticulierMaintenance && (state.customerDataRaw.driver === null || state.customerDataRaw.driver.company !== state.personalData.company)) {
      changedDriverInfo.customerCompany = state.personalData.company;
    }

    let changedChassieInfo = null;
    if (state.customerDataRaw.driver === null || state.customerDataRaw.driver.chassis !== state.chassie) {
      changedChassieInfo = state.chassie;
    }

    // console.log('RAW ', state.customerDataRaw);
    // console.log('EDIT ', state.personalData);
    // Company
    const company = {};
    company.type = state.vehiculeType;
    company.name = state.personalData.company !== '' ? state.personalData.company : 'QTEAM10';

    // Tires
    const tires = [];
    const tireTypes = ['MS', 'TZ', 'TS'];
    const tireLocations = ['1L1', '1R1', '2L2', '2L1', '2R1', '2R2'];

    const setTireInfo = (tireType, position, tireState, size) => {
      const tire = {
        position: position,
        dim1: size.width,
        serie: size.height,
        category: 'R',
        dim3: size.diameter,
        loadIndex: size.loadCapacity,
        speed: size.speedIndex,
        runOnFlat: state.runFlat,
        season: tireType,
        stock: tireState,
      };
      return tire;
    };

    tireLocations.forEach(tireLocation => {
      tireTypes.forEach(tireType => {
        const locationProperty = `t${tireLocation.toLowerCase()}`;
        const stockProperty = `${tireType.toLowerCase()}Stock`;
        const stockState = state.tireLocations[locationProperty][stockProperty];
        const isFrontTire = tireLocation.indexOf('2') === -1;
        const tireSize = isFrontTire ? state.tireSizes.frontTire : state.tireSizes
          .backTire;

        // Add active tires with correct tiretype
        // Note: Active MS tire can not be added when tiretype is not MS
        if ((stockState === 2 && state.tireType === tireType.toLowerCase()) ||
          stockState === 3) {
          tires.push(setTireInfo(tireType, tireLocation, stockState === 3,
            tireSize));
        }
      });
    });

    const appointmentType = determineAppointmentType(state);
    const details = {};
    details.remark = state.remark;
    details.language = Vue.config.language;
    switch (state.cat) {
      case 'depot':
        details.depot = state.depotId;
        details.slotNumber = state.slotNumber;
        details.appointmentType = appointmentType;
        break;
      case 'tag':
        details.address = state.addressId;
        details.date = state.slotDate.date;
        details.appointmentType = appointmentType;

        if (state.regionData.zipCode !== '' && state.regionData.city !== '' && state.regionData.address !== '') {
          details.location = `${state.regionData.address},${state.regionData.zipCode},${state.regionData.city}`;
        }

        if (state.timeOfArrival !== '') {
          details.remark = `${state.timeOfArrival}.${state.remark}`;
        }

        break;
      case 'tpp':
        details.rid = state.rid;
        details.slotNumber = state.slotNumber;
        break;
      default:
        break;
    }

    const vehicle = {
      licensePlate: state.licensePlate,
      chassisnr: changedChassieInfo,
      brand: state.vehicleData.brand,
      model: state.vehicleData.model,
      manufactureDate: state.vehicleData.manufactureDate,
      fuel: state.vehicleData.fuel,
      gearbox: state.vehicleData.gearbox,
    };

    let appointmentPostObject = {
      licensePlate: state.licensePlate,
      driver: changedDriverInfo,
      remark: state.remark,
      depotId: state.depotId,
      slotNumber: state.slotNumber,
      appointmentType: appointmentType,
      company: [company],
      tyres: tires,
      language: Vue.config.language,
    };

    // POST OBJECT FOR MOBILE COMPANY CALL
    if (state.cat !== '') {
      appointmentPostObject = {
        category: state.cat,
        details: details,
        vehicle: vehicle,
        driver: changedDriverInfo,
        company: [company],
        tyres: tires,
      };
    }

    // POST OBJECT FOR STANDARD FLOW
    return appointmentPostObject;
  },
  getRemark: state => state.remark,
  getCompany: state => state.company,
  getAppointment: state => state.appointment,
  getCustomerDataRaw: state => state.customerDataRaw,
  getAppointmentUpdateObject: state => {
    const appointmentUpdateObject = {
      deliveryNote: state.customerDataRaw.appointment.deliveryNote,
      reservation: {
        depot: state.depotId.toString(),
        date: state.slotDate,
        hour: state.slotHour,
        duration: state.slotDuration,
        tag: 'optional',
        language: getAppointmentUpdateLanguage(state),
      },
    };
    return appointmentUpdateObject;
  },
};

export default APPOINTMENT_GETTERS;
